import {Alert, Select, Skeleton, Space} from "antd";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {Link} from "react-router-dom";
import {FormField, FormFieldError} from "../../../../../design-system/form-field";
import type {SubSettingsFormFieldsType} from "../../../general/settings/sub-settings/types";
import {SubSettingsUrlRedirectLinkFormField} from "../../../general/settings/sub-settings/url-redirect/url-redirect-link-form-field";
import {TelegramIcon} from "../../../../../pages/dashboard/automations/icons";
import {CustomUrlLocationIcon} from "../../../location-select";
import {
  useGetIntegrationsStatus,
  useGetTelegramGroups,
} from "../../../../../react-query-hooks/integrations/integrations";

type RedirectAfterPurchaseOption = "none" | "telegram" | "custom-url";

const optionContentStyle = {
  alignItems: "center",
  display: "inline-flex",
  gap: 8,
};

export function RedirectAfterPurchaseFormFields() {
  const {control, setValue, clearErrors} = useFormContext<SubSettingsFormFieldsType>();
  const redirectAfterPurchase = useWatch({control, name: "redirectAfterPurchase"});
  const integrationsStatusQuery = useGetIntegrationsStatus({
    enabled: redirectAfterPurchase === "telegram",
  });
  const isTelegramConnected = integrationsStatusQuery.data?.telegramConnected === true;
  const telegramGroupsQuery = useGetTelegramGroups({
    enabled: redirectAfterPurchase === "telegram" && isTelegramConnected,
  });
  const shouldPromptTelegramConnection =
    redirectAfterPurchase === "telegram" &&
    integrationsStatusQuery.data?.telegramConnected === false;

  return (
    <Controller
      control={control}
      name="redirectAfterPurchase"
      defaultValue="none"
      render={({field: {onChange, value, ref}, fieldState: {error}}) => (
        <Space size={16} direction="vertical" style={{display: "flex"}}>
          <FormField
            id="redirect-after-purchase"
            label="Where should we send customers after purchase?"
            renderInput={(labelId) => (
              <Select<RedirectAfterPurchaseOption>
                id="redirect-after-purchase"
                aria-labelledby={labelId}
                ref={ref}
                size="large"
                value={value ?? "none"}
                style={{width: "100%"}}
                optionLabelProp="label"
                onChange={(nextValue) => {
                  onChange(nextValue);
                  const usesCustomUrl = nextValue === "custom-url";
                  setValue("isUrlRedirect", usesCustomUrl, {shouldDirty: true});
                  if (!usesCustomUrl) {
                    setValue("url_redirect", null, {shouldDirty: true});
                    clearErrors("url_redirect");
                  }
                  if (nextValue !== "telegram") {
                    setValue("telegramGroupId", undefined, {shouldDirty: true});
                  }
                }}
              >
                <Select.Option value="none" label="None">
                  None
                </Select.Option>
                <Select.Option
                  value="telegram"
                  label={
                    <span style={optionContentStyle}>
                      <TelegramIcon size={20} />
                      <span>Telegram</span>
                    </span>
                  }
                >
                  <span style={optionContentStyle}>
                    <TelegramIcon size={20} />
                    <span>Telegram</span>
                  </span>
                </Select.Option>
                <Select.Option
                  value="custom-url"
                  label={
                    <span style={optionContentStyle}>
                      <CustomUrlLocationIcon />
                      <span>Custom URL</span>
                    </span>
                  }
                >
                  <span style={optionContentStyle}>
                    <CustomUrlLocationIcon />
                    <span>Custom URL</span>
                  </span>
                </Select.Option>
              </Select>
            )}
          />
          <FormFieldError errorMessage={error?.message} />
          {shouldPromptTelegramConnection && (
            <Alert
              type="error"
              showIcon
              message={
                <span>
                  You haven&apos;t connected your Telegram account yet.{" "}
                  <Link
                    to="/automations/integrations/telegram"
                    style={{
                      color: "inherit",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    Connect Telegram
                  </Link>
                </span>
              }
            />
          )}
          {redirectAfterPurchase === "telegram" && isTelegramConnected && (
            <Controller
              control={control}
              name="telegramGroupId"
              render={({field: {onChange, value, ref}, fieldState: {error}}) => (
                <Space size={8} direction="vertical" style={{display: "flex"}}>
                  <FormField
                    id="telegram-group"
                    label="Select telegram group"
                    renderInput={(labelId) =>
                      telegramGroupsQuery.isLoading ? (
                        <Skeleton.Input active block size="large" style={{height: 40}} />
                      ) : (
                        <Select<string>
                          id="telegram-group"
                          aria-labelledby={labelId}
                          ref={ref}
                          size="large"
                          value={value}
                          placeholder="Please select"
                          style={{width: "100%"}}
                          onChange={onChange}
                          options={[
                            {label: "None", value: "none"},
                            ...(telegramGroupsQuery.data ?? []).map((group) => ({
                              label: group.chatTitle,
                              value: group.chatId,
                            })),
                          ]}
                        />
                      )
                    }
                  />
                  <FormFieldError errorMessage={error?.message} />
                </Space>
              )}
            />
          )}
          {value === "custom-url" && <SubSettingsUrlRedirectLinkFormField />}
        </Space>
      )}
    />
  );
}

// Backwards-compatible name for existing session and live-event forms.
export const SessionRedirectAfterPurchaseFormFields = RedirectAfterPurchaseFormFields;
